import React, { useState, useEffect, useRef } from 'react';
import { getImageUrl } from '../utils/imageUtils';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  MapPin, Calendar, Shield, Crown, Lock, MessageCircle, 
  Heart, Eye, Send, Unlock, User, CheckCircle, EyeOff, Users,
  ChevronLeft, ChevronRight, Star, PenLine, Video, Play, Share2,
  UserPlus, UserMinus, Ban, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { OnlineIndicator } from '../components/OnlineIndicator';
import PhotoComments from '../components/PhotoComments';
import SubscriptionPopup from '../components/SubscriptionPopup';
import ReportModal from '../components/ReportModal';

const DURATION_OPTIONS = [
  { value: '5min', label: '5 minutes' },
  { value: '1h', label: '1 heure' },
  { value: '24h', label: '24 heures' },
  { value: '3days', label: '3 jours' },
  { value: '1week', label: '1 semaine' },
  { value: 'unlimited', label: 'Illimité' }
];

const BODY_TYPE_LABELS = {
  mince: 'Mince',
  sportif: 'Sportif',
  moyenne: 'Dans la moyenne',
  rondeurs: 'Quelques rondeurs',
  rond: 'Rond'
};

const EXPERIENCE_LABELS = {
  debutant: 'Débutant',
  occasionnel: 'Occasionnel',
  experimente: 'Expérimenté'
};

const SMOKER_LABELS = {
  non: 'Non fumeur',
  non_fumeur: 'Non fumeur',
  occasionnel: 'Fumeur occasionnel',
  oui: 'Fumeur',
  fumeur: 'Fumeur',
  vapoteur: 'Vapoteur'
};

const EYE_COLOR_LABELS = {
  noirs: 'Noirs',
  marrons: 'Marrons',
  verts: 'Verts',
  bleus: 'Bleus'
};

const ORIENTATION_LABELS = {
  hetero: 'Hétéro',
  hetero_curieux: 'Hétéro curieux',
  bi: 'Bi',
  lesbienne: 'Lesbienne',
  gay: 'Gay'
};

export default function MemberProfilePage() {
  const { userId } = useParams();
  const { user, api } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
  const [selectedPhotoType, setSelectedPhotoType] = useState('public'); // 'public' or 'private'
  const [requestMessage, setRequestMessage] = useState('');
  const [requestingAccess, setRequestingAccess] = useState(false);
  
  // Video state
  const [selectedVideo, setSelectedVideo] = useState(null);
  const videoRef = useRef(null);
  
  // Albums state
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [selectedAlbumPhotoIndex, setSelectedAlbumPhotoIndex] = useState(null);
  
  // Share private albums state
  const [showShareModal, setShowShareModal] = useState(false);
  const [myPrivateAlbums, setMyPrivateAlbums] = useState([]);
  const [shareMode, setShareMode] = useState('all'); // 'all' or 'specific'
  const [selectedAlbumsToShare, setSelectedAlbumsToShare] = useState({});
  const [shareDurations, setShareDurations] = useState({});
  const [allShareDuration, setAllShareDuration] = useState('unlimited');
  const [sharingAccess, setSharingAccess] = useState(false);
  
  // Testimonials state
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialContent, setTestimonialContent] = useState('');
  const [submittingTestimonial, setSubmittingTestimonial] = useState(false);
  const [showTestimonialForm, setShowTestimonialForm] = useState(false);
  
  // Follow state
  const [isFollowing, setIsFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [followLoading, setFollowLoading] = useState(false);
  
  // Subscription popup state
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);

  // Block state
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);
  
  // Report state
  const [showReportModal, setShowReportModal] = useState(false);

  // Get the current photos array based on type
  const getCurrentPhotos = () => {
    if (!profile) return [];
    return selectedPhotoType === 'public' ? (profile.photos || []) : (profile.private_photos || []);
  };

  const selectedPhoto = selectedPhotoIndex !== null ? getCurrentPhotos()[selectedPhotoIndex] : null;

  const openPhotoModal = (index, type = 'public') => {
    setSelectedPhotoType(type);
    setSelectedPhotoIndex(index);
  };

  const closePhotoModal = () => {
    setSelectedPhotoIndex(null);
  };

  const openVideoModal = (video) => {
    setSelectedVideo(video);
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const likeVideo = async (videoId, e) => {
    e.stopPropagation();
    try {
      await api.post(`/videos/${videoId}/like`);
      // Update local state
      setProfile(prev => ({
        ...prev,
        videos: prev.videos?.map(v => 
          v.id === videoId ? { ...v, likes: (v.likes || 0) + 1 } : v
        )
      }));
      toast.success('Vidéo likée !');
    } catch (error) {
      toast.error('Erreur lors du like');
    }
  };

  const goToPreviousPhoto = (e) => {
    e.stopPropagation();
    const photos = getCurrentPhotos();
    if (selectedPhotoIndex > 0) {
      setSelectedPhotoIndex(selectedPhotoIndex - 1);
    } else {
      setSelectedPhotoIndex(photos.length - 1); // Loop to last
    }
  };

  const goToNextPhoto = (e) => {
    e.stopPropagation();
    const photos = getCurrentPhotos();
    if (selectedPhotoIndex < photos.length - 1) {
      setSelectedPhotoIndex(selectedPhotoIndex + 1);
    } else {
      setSelectedPhotoIndex(0); // Loop to first
    }
  };

  // Album photo navigation
  const getAlbumPhotos = () => {
    if (!selectedAlbum) return [];
    return [...(selectedAlbum.media || []), ...(selectedAlbum.photos || [])];
  };

  const selectedAlbumPhoto = selectedAlbumPhotoIndex !== null ? getAlbumPhotos()[selectedAlbumPhotoIndex] : null;

  const openAlbumPhotoModal = (index) => {
    setSelectedAlbumPhotoIndex(index);
  };

  const closeAlbumPhotoModal = () => {
    setSelectedAlbumPhotoIndex(null);
  };

  const goToPreviousAlbumPhoto = (e) => {
    e.stopPropagation();
    const photos = getAlbumPhotos();
    if (selectedAlbumPhotoIndex > 0) {
      setSelectedAlbumPhotoIndex(selectedAlbumPhotoIndex - 1);
    } else {
      setSelectedAlbumPhotoIndex(photos.length - 1);
    }
  };

  const goToNextAlbumPhoto = (e) => {
    e.stopPropagation();
    const photos = getAlbumPhotos();
    if (selectedAlbumPhotoIndex < photos.length - 1) {
      setSelectedAlbumPhotoIndex(selectedAlbumPhotoIndex + 1);
    } else {
      setSelectedAlbumPhotoIndex(0);
    }
  };

  useEffect(() => {
    fetchProfile();
    fetchTestimonials();
    fetchAlbums();
    fetchFollowStatus();
    fetchBlockStatus();
  }, [userId]);

  // Define isOwnProfile early for use in other effects
  const isOwnProfile = userId === user.id;

  // Show subscription popup for non-subscribers after a delay
  useEffect(() => {
    if (!user.is_subscribed && profile && !isOwnProfile) {
      const timer = setTimeout(() => {
        // Check if popup was already shown in this session
        const popupShown = sessionStorage.getItem(`subscription_popup_shown_${userId}`);
        if (!popupShown) {
          setShowSubscriptionPopup(true);
          sessionStorage.setItem(`subscription_popup_shown_${userId}`, 'true');
        }
      }, 2000); // Show after 2 seconds
      return () => clearTimeout(timer);
    }
  }, [user.is_subscribed, profile, userId, isOwnProfile]);

  const fetchFollowStatus = async () => {
    try {
      const response = await api.get(`/users/${userId}/follow/status`);
      setIsFollowing(response.data.is_following);
      setFollowersCount(response.data.followers_count);
      setFollowingCount(response.data.following_count);
    } catch (error) {
      console.error('Error fetching follow status:', error);
    }
  };

  const handleFollow = async () => {
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await api.delete(`/users/${userId}/follow`);
        setIsFollowing(false);
        setFollowersCount(prev => prev - 1);
        toast.success(`Vous ne suivez plus ${profile?.pseudo}`);
      } else {
        await api.post(`/users/${userId}/follow`);
        setIsFollowing(true);
        setFollowersCount(prev => prev + 1);
        toast.success(`Vous suivez maintenant ${profile?.pseudo}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'opération');
    } finally {
      setFollowLoading(false);
    }
  };

  // Fetch block status
  const fetchBlockStatus = async () => {
    try {
      const response = await api.get(`/users/${userId}/block/status`);
      setIsBlocked(response.data.i_blocked_them);
    } catch (error) {
      console.error('Error fetching block status:', error);
    }
  };

  // Handle block/unblock
  const handleBlock = async () => {
    if (isBlocked) {
      // Unblock
      if (!window.confirm(`Êtes-vous sûr de vouloir débloquer ${profile?.pseudo} ?`)) {
        return;
      }
      setBlockLoading(true);
      try {
        await api.delete(`/users/${userId}/block`);
        setIsBlocked(false);
        toast.success(`${profile?.pseudo} a été débloqué`);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors du déblocage');
      } finally {
        setBlockLoading(false);
      }
    } else {
      // Block
      if (!window.confirm(`Êtes-vous sûr de vouloir bloquer ${profile?.pseudo} ? Ce profil ne pourra plus voir le vôtre.`)) {
        return;
      }
      setBlockLoading(true);
      try {
        await api.post(`/users/${userId}/block`);
        setIsBlocked(true);
        setIsFollowing(false); // Also unfollow when blocking
        toast.success(`${profile?.pseudo} a été bloqué`);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors du blocage');
      } finally {
        setBlockLoading(false);
      }
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await api.get(`/profile/${userId}`);
      setProfile(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du profil');
    } finally {
      setLoading(false);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await api.get(`/testimonials/${userId}`);
      setTestimonials(response.data);
    } catch (error) {
      // Silently fail - testimonials are optional
    }
  };

  const fetchAlbums = async () => {
    try {
      const response = await api.get(`/albums/user/${userId}`);
      setAlbums(response.data);
    } catch (error) {
      // Silently fail - albums are optional
    }
  };

  const fetchAlbumDetails = async (albumId) => {
    try {
      const response = await api.get(`/albums/${albumId}`);
      setSelectedAlbum(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'album');
    }
  };

  const fetchMyPrivateAlbums = async () => {
    try {
      const response = await api.get('/albums');
      const privateAlbums = response.data.filter(a => a.is_private);
      setMyPrivateAlbums(privateAlbums);
      // Initialize durations
      const durations = {};
      privateAlbums.forEach(album => {
        durations[album.id] = 'unlimited';
      });
      setShareDurations(durations);
    } catch (error) {
      console.error('Error fetching private albums:', error);
    }
  };

  const openShareModal = () => {
    fetchMyPrivateAlbums();
    setShareMode('all');
    setSelectedAlbumsToShare({});
    setAllShareDuration('unlimited');
    setShowShareModal(true);
  };

  const handleAlbumSelectionChange = (albumId, checked) => {
    setSelectedAlbumsToShare(prev => ({
      ...prev,
      [albumId]: checked
    }));
  };

  const handleAlbumDurationChange = (albumId, duration) => {
    setShareDurations(prev => ({
      ...prev,
      [albumId]: duration
    }));
  };

  const sharePrivateAccess = async () => {
    setSharingAccess(true);
    try {
      if (shareMode === 'all') {
        await api.post('/album-access/share', {
          target_user_id: userId,
          share_all: true,
          album_ids: [],
          duration: allShareDuration
        });
        toast.success(`Accès à tous vos albums privés accordé à ${profile.pseudo}`);
      } else {
        // Get selected albums
        const selectedIds = Object.entries(selectedAlbumsToShare)
          .filter(([_, selected]) => selected)
          .map(([id, _]) => id);
        
        if (selectedIds.length === 0) {
          toast.error('Veuillez sélectionner au moins un album');
          setSharingAccess(false);
          return;
        }
        
        // For specific albums, use the first selected album's duration
        // (In a more complex implementation, you could send multiple requests per album)
        const firstSelectedId = selectedIds[0];
        const duration = shareDurations[firstSelectedId] || 'unlimited';
        
        await api.post('/album-access/share', {
          target_user_id: userId,
          share_all: false,
          album_ids: selectedIds,
          duration: duration
        });
        toast.success(`Accès accordé à ${profile.pseudo} pour ${selectedIds.length} album(s)`);
      }
      setShowShareModal(false);
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors du partage';
      toast.error(message);
    } finally {
      setSharingAccess(false);
    }
  };

  const submitTestimonial = async () => {
    if (!testimonialContent.trim() || testimonialContent.length < 10) {
      toast.error('Le témoignage doit contenir au moins 10 caractères');
      return;
    }
    
    setSubmittingTestimonial(true);
    try {
      await api.post(`/testimonials/${userId}`, { content: testimonialContent });
      toast.success('Témoignage envoyé ! Il sera visible après validation par le membre.');
      setTestimonialContent('');
      setShowTestimonialForm(false);
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'envoi';
      toast.error(message);
    } finally {
      setSubmittingTestimonial(false);
    }
  };

  const requestPhotoAccess = async () => {
    if (requestingAccess) return;
    setRequestingAccess(true);
    
    try {
      await api.post('/photos/request-access', {
        target_user_id: userId,
        message: requestMessage || null
      });
      toast.success('Demande d\'accès envoyée !');
      setRequestMessage('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la demande';
      toast.error(message);
    } finally {
      setRequestingAccess(false);
    }
  };

  const likePhoto = async (photoId) => {
    try {
      await api.post(`/photos/${photoId}/like`);
      toast.success('Photo likée');
      
      // Update local state for immediate feedback
      // Update in profile photos
      setProfile(prev => ({
        ...prev,
        photos: prev.photos?.map(p => 
          p.id === photoId ? { ...p, likes: (p.likes || 0) + 1 } : p
        ),
        private_photos: prev.private_photos?.map(p => 
          p.id === photoId ? { ...p, likes: (p.likes || 0) + 1 } : p
        )
      }));
      
      // Update in album if viewing album
      if (selectedAlbum) {
        setSelectedAlbum(prev => ({
          ...prev,
          media: prev.media?.map(m => 
            m.id === photoId ? { ...m, likes: (m.likes || 0) + 1 } : m
          ),
          photos: prev.photos?.map(p => 
            p.id === photoId ? { ...p, likes: (p.likes || 0) + 1 } : p
          )
        }));
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur';
      toast.error(message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark pt-24 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-dark pt-24 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-serif gold-gradient-text mb-4">Profil non trouvé</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark pt-24 pb-12" data-testid="member-profile-page">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header Section */}
        <div className="glass-card p-8 mb-8">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Profile Picture */}
            <div className="relative">
              <div className="w-40 h-40 rounded-full overflow-hidden bg-dark-subtle border-4 border-gold/30">
                {profile.profile_picture ? (
                  <img 
                    src={getImageUrl(profile.profile_picture)} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="w-12 h-12 text-gray-500" />
                  </div>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-serif gold-gradient-text inline-flex items-center gap-2">
                  {profile.pseudo}
                  {profile.is_online && (
                    <OnlineIndicator isOnline={true} size="lg" />
                  )}
                </h1>
                {profile.is_online && (
                  <span className="text-green-400 text-xs font-medium">En ligne</span>
                )}
                {profile.is_ambassador && (
                  <span className="flex items-center gap-1 px-2 py-1 bg-gold text-dark text-xs font-semibold">
                    <Crown className="w-3 h-3" /> Ambassadeur
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-4 text-sm text-gray-400 mb-4">
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" /> {profile.location}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" /> {profile.age} ans
                </span>
                <span className="capitalize">{profile.profile_type}</span>
              </div>

              <div className="flex gap-4 text-sm mb-6">
                <div className="text-center">
                  <p className="text-gold font-semibold">{profile.photos?.length || 0}</p>
                  <p className="text-gray-500">Photos</p>
                </div>
                <div className="text-center">
                  <p className="text-gold font-semibold">{profile.likes_count || 0}</p>
                  <p className="text-gray-500">Likes</p>
                </div>
              </div>

              {/* Actions */}
              {!isOwnProfile && user.is_subscribed && (
                <div className="space-y-4">
                  {/* Top row: Follow, Message, Chat */}
                  <div className="flex gap-3 flex-wrap">
                    {/* Follow Button */}
                    <Button
                      onClick={handleFollow}
                      disabled={followLoading}
                      className={`h-12 px-6 flex items-center justify-center gap-2 font-semibold uppercase tracking-wide text-sm ${isFollowing ? 'btn-outline-gold' : 'btn-gold'}`}
                      data-testid="follow-btn"
                    >
                      {followLoading ? (
                        <div className="w-5 h-5 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                      ) : isFollowing ? (
                        <UserMinus className="w-5 h-5" />
                      ) : (
                        <UserPlus className="w-5 h-5" />
                      )}
                      {isFollowing ? 'Suivi' : 'Suivre'}
                    </Button>
                    
                    {/* Message Button */}
                    <Link 
                      to={`/messages`} 
                      state={{ recipientId: userId }}
                      className="h-12 px-6 flex items-center justify-center gap-2 font-semibold uppercase tracking-wide text-sm btn-gold"
                      data-testid="send-message-btn"
                    >
                      <MessageCircle className="w-5 h-5" />
                      Envoyer un message
                    </Link>
                    
                    {/* Chat Button */}
                    <Link 
                      to={`/chat/${userId}`}
                      className="h-12 px-6 flex items-center justify-center gap-2 font-semibold uppercase tracking-wide text-sm btn-outline-gold"
                      data-testid="start-chat-btn"
                    >
                      <Send className="w-5 h-5" />
                      Chat
                    </Link>
                  </div>
                  
                  {/* Bottom row: Share albums - full width */}
                  <Button
                    onClick={openShareModal}
                    className="w-full h-12 px-6 flex items-center justify-center gap-2 font-semibold uppercase tracking-wide text-sm btn-outline-gold"
                    data-testid="share-private-btn"
                  >
                    <Share2 className="w-5 h-5" />
                    Partager mes albums privés
                  </Button>
                </div>
              )}

              {/* Follow button for non-subscribers */}
              {!isOwnProfile && !user.is_subscribed && (
                <div className="space-y-3">
                  <Button
                    onClick={handleFollow}
                    disabled={followLoading}
                    className={`flex items-center gap-2 ${isFollowing ? 'btn-outline-gold' : 'btn-gold'}`}
                    data-testid="follow-btn"
                  >
                    {followLoading ? (
                      <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                    ) : isFollowing ? (
                      <UserMinus className="w-4 h-4" />
                    ) : (
                      <UserPlus className="w-4 h-4" />
                    )}
                    {isFollowing ? 'Suivi' : 'Suivre'}
                  </Button>
                  
                  <div className="p-4 bg-dark-subtle border border-white/10 rounded-sm">
                    <Lock className="w-5 h-5 text-gold inline mr-2" />
                    <span className="text-gray-400 text-sm">
                      <Link to="/subscription" className="text-gold hover:underline">
                        Abonnez-vous
                      </Link>{' '}
                      pour contacter ce membre
                    </span>
                  </div>
                </div>
              )}

              {/* Block & Report buttons - shown for all non-own profiles */}
              {!isOwnProfile && (
                <div className="flex gap-3 mt-4 pt-4 border-t border-white/10">
                  <Button
                    onClick={handleBlock}
                    disabled={blockLoading}
                    variant="outline"
                    className={`flex-1 flex items-center justify-center gap-2 text-sm ${
                      isBlocked 
                        ? 'border-green-500/50 text-green-400 hover:bg-green-500/10' 
                        : 'border-red-500/50 text-red-400 hover:bg-red-500/10'
                    }`}
                    data-testid="block-btn"
                  >
                    {blockLoading ? (
                      <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                    ) : (
                      <Ban className="w-4 h-4" />
                    )}
                    {isBlocked ? 'Débloquer' : 'Bloquer'}
                  </Button>
                  
                  <Button
                    onClick={() => setShowReportModal(true)}
                    variant="outline"
                    className="flex-1 flex items-center justify-center gap-2 text-sm border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                    data-testid="report-btn"
                  >
                    <AlertTriangle className="w-4 h-4" />
                    Signaler
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="mt-8 pt-8 border-t border-white/10">
            <h3 className="text-gold text-sm uppercase tracking-widest mb-3">À propos</h3>
            {profile.description_blurred ? (
              <div className="relative">
                <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">{profile.description}</p>
                <div className="absolute inset-0 flex items-end justify-center bg-gradient-to-t from-dark via-dark/80 to-transparent pt-8">
                  <Link to="/subscription" className="flex items-center gap-2 text-gold hover:text-gold-light mb-2">
                    <Crown className="w-4 h-4" /> Abonnez-vous pour lire la suite
                  </Link>
                </div>
              </div>
            ) : (
              <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">{profile.description}</p>
            )}
          </div>

          {/* Preferences */}
          {profile.sexual_preferences?.length > 0 && (
            <div className="mt-6">
              <h3 className="text-gold text-sm uppercase tracking-widest mb-3">Envies</h3>
              <div className="flex flex-wrap gap-2">
                {profile.sexual_preferences.map(pref => (
                  <span key={pref} className="px-3 py-1 bg-gold/10 text-gold text-xs border border-gold/30">
                    {pref}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {profile.is_blurred && !profile.sexual_preferences?.length && (
            <div className="mt-6">
              <h3 className="text-gold text-sm uppercase tracking-widest mb-3">Envies</h3>
              <div className="flex items-center gap-2 text-gray-500">
                <EyeOff className="w-4 h-4" />
                <span className="text-sm">Visible pour les membres Ambassadeur</span>
              </div>
            </div>
          )}

          {/* Physical Info Section - for individual profiles */}
          {profile.profile_type !== 'couple' && (
            <div className="mt-8 pt-8 border-t border-white/10" data-testid="profile-info-section">
              <h3 className="text-gold text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
                <User className="w-4 h-4" /> Informations
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {/* Height */}
                {profile.height && (
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-gray-500 text-xs uppercase mb-1">Taille</p>
                    <p className="text-white font-medium">{profile.height} cm</p>
                  </div>
                )}
                
                {/* Body Type */}
                {profile.body_type && (
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-gray-500 text-xs uppercase mb-1">Physique</p>
                    <p className="text-white font-medium">{BODY_TYPE_LABELS[profile.body_type] || profile.body_type}</p>
                  </div>
                )}
                
                {/* Ethnicity */}
                {profile.ethnicity && (
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-gray-500 text-xs uppercase mb-1">Origine</p>
                    <p className="text-white font-medium capitalize">{profile.ethnicity}</p>
                  </div>
                )}
                
                {/* Eye Color */}
                {profile.eye_color && (
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-gray-500 text-xs uppercase mb-1">Yeux</p>
                    <p className="text-white font-medium">{EYE_COLOR_LABELS[profile.eye_color] || profile.eye_color}</p>
                  </div>
                )}
                
                {/* Sexual Orientation */}
                {profile.sexual_orientation && (
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-gray-500 text-xs uppercase mb-1">Orientation</p>
                    <p className="text-white font-medium">{ORIENTATION_LABELS[profile.sexual_orientation] || profile.sexual_orientation}</p>
                  </div>
                )}
                
                {/* Smoker Status */}
                {profile.smoker && (
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-gray-500 text-xs uppercase mb-1">Tabac</p>
                    <p className="text-white font-medium">{SMOKER_LABELS[profile.smoker] || profile.smoker}</p>
                  </div>
                )}
                
                {/* Experience Level */}
                {profile.experience_level && (
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-gray-500 text-xs uppercase mb-1">Expérience</p>
                    <p className="text-white font-medium">{EXPERIENCE_LABELS[profile.experience_level] || profile.experience_level}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Couple Details Section */}
          {profile.profile_type === 'couple' && (profile.couple_woman || profile.couple_man) && (
            <div className="mt-8 pt-8 border-t border-white/10" data-testid="couple-details-section">
              <h3 className="text-gold text-sm uppercase tracking-widest mb-6 flex items-center gap-2">
                <Users className="w-4 h-4" /> Détails du couple
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Elle (Woman) */}
                {profile.couple_woman && (
                  <div className="bg-gradient-to-br from-pink-500/10 to-transparent border border-pink-500/20 rounded-lg p-5" data-testid="couple-woman-details">
                    <h4 className="text-pink-400 font-semibold mb-4 flex items-center gap-2">
                      <span className="text-lg">♀</span> Elle
                    </h4>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Âge</span>
                        <span className="text-white">{profile.couple_woman.age} ans</span>
                      </div>
                      {profile.couple_woman.height && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Taille</span>
                          <span className="text-white">{profile.couple_woman.height} cm</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-500">Physique</span>
                        <span className="text-white">{BODY_TYPE_LABELS[profile.couple_woman.body_type] || profile.couple_woman.body_type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Origine</span>
                        <span className="text-white capitalize">{profile.couple_woman.ethnicity}</span>
                      </div>
                      {profile.couple_woman.eye_color && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Yeux</span>
                          <span className="text-white">{EYE_COLOR_LABELS[profile.couple_woman.eye_color] || profile.couple_woman.eye_color}</span>
                        </div>
                      )}
                      {profile.couple_woman.sexual_orientation && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Orientation</span>
                          <span className="text-white">{ORIENTATION_LABELS[profile.couple_woman.sexual_orientation] || profile.couple_woman.sexual_orientation}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-500">Expérience</span>
                        <span className="text-white">{EXPERIENCE_LABELS[profile.couple_woman.experience_level] || profile.couple_woman.experience_level}</span>
                      </div>
                      {profile.couple_woman.smoker && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Fumeur</span>
                          <span className="text-white">{SMOKER_LABELS[profile.couple_woman.smoker] || profile.couple_woman.smoker}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Lui (Man) */}
                {profile.couple_man && (
                  <div className="bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/20 rounded-lg p-5" data-testid="couple-man-details">
                    <h4 className="text-blue-400 font-semibold mb-4 flex items-center gap-2">
                      <span className="text-lg">♂</span> Lui
                    </h4>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Âge</span>
                        <span className="text-white">{profile.couple_man.age} ans</span>
                      </div>
                      {profile.couple_man.height && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Taille</span>
                          <span className="text-white">{profile.couple_man.height} cm</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-500">Physique</span>
                        <span className="text-white">{BODY_TYPE_LABELS[profile.couple_man.body_type] || profile.couple_man.body_type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Origine</span>
                        <span className="text-white capitalize">{profile.couple_man.ethnicity}</span>
                      </div>
                      {profile.couple_man.eye_color && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Yeux</span>
                          <span className="text-white">{EYE_COLOR_LABELS[profile.couple_man.eye_color] || profile.couple_man.eye_color}</span>
                        </div>
                      )}
                      {profile.couple_man.sexual_orientation && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Orientation</span>
                          <span className="text-white">{ORIENTATION_LABELS[profile.couple_man.sexual_orientation] || profile.couple_man.sexual_orientation}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-500">Expérience</span>
                        <span className="text-white">{EXPERIENCE_LABELS[profile.couple_man.experience_level] || profile.couple_man.experience_level}</span>
                      </div>
                      {profile.couple_man.smoker && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Fumeur</span>
                          <span className="text-white">{SMOKER_LABELS[profile.couple_man.smoker] || profile.couple_man.smoker}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Galerie Section */}
        {profile.is_blurred ? (
          <div className="glass-card p-12 text-center">
            <Lock className="w-12 h-12 text-gold mx-auto mb-4" />
            <h3 className="font-serif text-xl text-white mb-2">Galerie verrouillée</h3>
            <p className="text-gray-400 mb-2">
              Accédez à toutes les fonctionnalités de DiscreetLux
            </p>
            <p className="text-gray-500 text-sm mb-6">
              Photos, messagerie, chat en direct, recherche avancée...
            </p>
            <Link to="/subscription" className="btn-gold inline-flex items-center gap-2">
              <Crown className="w-4 h-4" />
              Abonnez-vous
            </Link>
          </div>
        ) : (
          <>
            {/* Galerie Title */}
            <h2 className="font-serif text-2xl text-gold mb-6">Galerie</h2>

            {/* Photos et Vidéos Section */}
            <div className="mb-10">
              <h3 className="text-gold text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
                <Eye className="w-4 h-4" /> Photos et Vidéos
              </h3>
              
              {(profile.photos?.length > 0 || profile.videos?.length > 0) ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="gallery-media">
                  {/* Photos */}
                  {profile.photos?.map((photo, index) => (
                    <div 
                      key={photo.id || `photo-${index}`}
                      className="aspect-square relative group cursor-pointer overflow-hidden rounded-sm bg-dark-subtle"
                      onClick={() => openPhotoModal(index, 'public')}
                    >
                      <img
                        src={getImageUrl(photo.url)}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <Eye className="w-8 h-8 text-white" />
                      </div>
                      <div className="absolute bottom-2 left-2 flex items-center gap-2 text-xs text-white">
                        <span className="flex items-center gap-1 bg-black/50 px-2 py-1 rounded">
                          <Heart className="w-3 h-3" /> {photo.likes || 0}
                        </span>
                      </div>
                    </div>
                  ))}
                  
                  {/* Videos */}
                  {user?.is_subscribed && profile.videos?.map((video, index) => (
                    <div 
                      key={video.id || `video-${index}`}
                      className="aspect-square relative group cursor-pointer overflow-hidden rounded-sm bg-dark-subtle"
                      onClick={() => openVideoModal(video)}
                    >
                      <video
                        src={getImageUrl(video.url)}
                        className="w-full h-full object-cover"
                        muted
                        preload="metadata"
                      />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/60 transition-colors">
                        <Play className="w-10 h-10 text-white" />
                      </div>
                      <div className="absolute top-2 left-2">
                        <Video className="w-4 h-4 text-gold" />
                      </div>
                      <div className="absolute bottom-2 left-2 flex items-center gap-2 text-xs text-white">
                        <button 
                          onClick={(e) => likeVideo(video.id, e)}
                          className="flex items-center gap-1 bg-black/50 px-2 py-1 rounded hover:bg-black/70 transition-colors"
                        >
                          <Heart className="w-3 h-3" /> {video.likes || 0}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="glass-card p-8 text-center text-gray-500">
                  Aucune photo ou vidéo
                </div>
              )}
            </div>

            {/* Albums Section */}
            <div className="mb-10">
              <h3 className="text-gold text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
                <Users className="w-4 h-4" /> Albums
              </h3>
              
              {albums.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="albums-grid">
                  {albums.map((album) => {
                    const mediaCount = (album.media?.length || 0) + (album.photos?.length || 0);
                    // Allow access if: not private, OR user has private access, OR album was shared with current user
                    const canAccess = !album.is_private || profile.has_private_access || album.shared_with_you;
                    
                    return (
                      <div 
                        key={album.id}
                        className={`glass-card overflow-hidden group cursor-pointer hover:border-gold/30 transition-colors ${!canAccess ? 'opacity-75' : ''}`}
                        onClick={() => canAccess && fetchAlbumDetails(album.id)}
                        data-testid={`album-card-${album.id}`}
                      >
                        <div className="aspect-video bg-dark-subtle relative">
                          {album.cover_url ? (
                            <img
                              src={getImageUrl(album.cover_url)}
                              alt={album.name}
                              className={`w-full h-full object-cover ${!canAccess ? 'blur-sm' : ''}`}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Users className="w-12 h-12 text-gray-600" />
                            </div>
                          )}
                          <div className="absolute top-2 right-2">
                            {album.is_private ? (
                              <span className="px-2 py-1 bg-black/60 rounded text-xs text-gold flex items-center gap-1">
                                <Lock className="w-3 h-3" /> Privé
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-black/60 rounded text-xs text-green-500 flex items-center gap-1">
                                <Unlock className="w-3 h-3" /> Public
                              </span>
                            )}
                          </div>
                          {!canAccess && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                              <Lock className="w-8 h-8 text-gold" />
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <h3 className="font-semibold text-white truncate">{album.name}</h3>
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {mediaCount} média{mediaCount !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="glass-card p-8 text-center text-gray-500">
                  Aucun album
                </div>
              )}
            </div>
            
            {/* Testimonials Section */}
            <div className="mt-8 pt-8 border-t border-white/10" data-testid="testimonials-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-xl text-white flex items-center gap-2">
                  <Star className="w-5 h-5 text-gold" /> Témoignages
                  <span className="text-sm text-gray-500">({testimonials.length})</span>
                </h2>
                {!isOwnProfile && user?.is_subscribed && (
                  <Button
                    onClick={() => setShowTestimonialForm(!showTestimonialForm)}
                    className="btn-outline-gold flex items-center gap-2 text-sm"
                    data-testid="write-testimonial-btn"
                  >
                    <PenLine className="w-4 h-4" />
                    Écrire un témoignage
                  </Button>
                )}
              </div>
              
              {/* Testimonial Form */}
              {showTestimonialForm && (
                <div className="glass-card p-4 mb-6" data-testid="testimonial-form">
                  <h3 className="text-gold text-sm uppercase tracking-wider mb-3">
                    Laisser un témoignage pour {profile.pseudo}
                  </h3>
                  <Textarea
                    placeholder="Partagez votre expérience avec ce membre... (min. 10 caractères)"
                    value={testimonialContent}
                    onChange={(e) => setTestimonialContent(e.target.value)}
                    className="bg-dark border-white/10 text-white placeholder:text-gray-500 min-h-[100px] mb-3"
                    maxLength={500}
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{testimonialContent.length}/500</span>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          setShowTestimonialForm(false);
                          setTestimonialContent('');
                        }}
                        variant="outline"
                        className="text-gray-400 border-gray-600"
                      >
                        Annuler
                      </Button>
                      <Button
                        onClick={submitTestimonial}
                        disabled={submittingTestimonial || testimonialContent.length < 10}
                        className="btn-gold"
                      >
                        {submittingTestimonial ? 'Envoi...' : 'Envoyer'}
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Votre témoignage sera soumis à validation par le membre avant d'être publié.
                  </p>
                </div>
              )}
              
              {/* Testimonials List */}
              {testimonials.length > 0 ? (
                <div className="space-y-4">
                  {testimonials.map((testimonial) => (
                    <div key={testimonial.id} className="glass-card p-4" data-testid="testimonial-card">
                      <div className="flex items-start gap-3">
                        <Link to={`/member/${testimonial.author_id}`}>
                          {testimonial.author_profile_picture ? (
                            <img 
                              src={getImageUrl(testimonial.author_profile_picture)}
                              alt={testimonial.author_pseudo}
                              className="w-10 h-10 rounded-full object-cover border border-gold/30"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center text-gold font-semibold">
                              {testimonial.author_pseudo?.[0]?.toUpperCase() || '?'}
                            </div>
                          )}
                        </Link>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Link 
                              to={`/member/${testimonial.author_id}`}
                              className="font-semibold text-gold hover:underline"
                            >
                              {testimonial.author_pseudo}
                            </Link>
                            <span className="text-xs text-gray-500">
                              {new Date(testimonial.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </span>
                          </div>
                          <p className="text-gray-300 text-sm leading-relaxed">
                            "{testimonial.content}"
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="glass-card p-8 text-center text-gray-500">
                  <Star className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Aucun témoignage pour le moment</p>
                  {!isOwnProfile && user?.is_subscribed && (
                    <p className="text-sm mt-2">Soyez le premier à laisser un témoignage !</p>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* Photo Modal with Navigation */}
        <Dialog open={selectedPhotoIndex !== null} onOpenChange={(open) => !open && closePhotoModal()}>
          <DialogContent className="bg-dark-paper border-white/10 max-w-5xl max-h-[90vh] overflow-hidden p-0">
            {selectedPhoto && (
              <div className="flex flex-col md:flex-row h-full">
                {/* Photo with Navigation */}
                <div className="relative flex-1 flex items-center justify-center bg-black min-h-[300px] md:min-h-[500px]">
                  {/* Previous button */}
                  {getCurrentPhotos().length > 1 && (
                    <button
                      onClick={goToPreviousPhoto}
                      className="absolute left-2 md:left-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
                      data-testid="prev-photo-btn"
                    >
                      <ChevronLeft className="w-6 h-6 md:w-8 md:h-8" />
                    </button>
                  )}
                  
                  {/* Photo */}
                  <img
                    src={getImageUrl(selectedPhoto.url)}
                    alt="Photo"
                    className="max-w-full max-h-[50vh] md:max-h-[80vh] object-contain"
                  />
                  
                  {/* Next button */}
                  {getCurrentPhotos().length > 1 && (
                    <button
                      onClick={goToNextPhoto}
                      className="absolute right-2 md:right-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
                      data-testid="next-photo-btn"
                    >
                      <ChevronRight className="w-6 h-6 md:w-8 md:h-8" />
                    </button>
                  )}
                  
                  {/* Photo counter */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/60 rounded-full text-white text-sm">
                    {selectedPhotoIndex + 1} / {getCurrentPhotos().length}
                  </div>
                </div>
                
                {/* Comments section */}
                <div className="w-full md:w-80 lg:w-96 flex flex-col p-4 md:p-6 border-t md:border-t-0 md:border-l border-white/10 max-h-[40vh] md:max-h-none overflow-y-auto">
                  {/* Photo info */}
                  <div className="flex items-center justify-between pb-4 border-b border-white/10">
                    <span className="flex items-center gap-2 text-gray-400">
                      <Heart className="w-4 h-4" /> {selectedPhoto.likes || 0} likes
                    </span>
                    {user?.is_subscribed && !isOwnProfile && (
                      <Button 
                        onClick={() => likePhoto(selectedPhoto.id)}
                        className="btn-outline-gold flex items-center gap-2 text-sm"
                      >
                        <Heart className="w-4 h-4" /> Like
                      </Button>
                    )}
                  </div>
                  
                  {/* Comments */}
                  <PhotoComments 
                    photoId={selectedPhoto.id} 
                    photoOwnerId={profile.user_id}
                  />
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Video Modal */}
        <Dialog open={selectedVideo !== null} onOpenChange={(open) => !open && closeVideoModal()}>
          <DialogContent className="bg-dark-paper border-white/10 max-w-4xl max-h-[90vh] overflow-hidden p-0">
            {selectedVideo && (
              <div className="flex flex-col">
                {/* Video Player */}
                <div className="relative bg-black flex items-center justify-center">
                  <video
                    ref={videoRef}
                    src={getImageUrl(selectedVideo.url)}
                    className="max-w-full max-h-[70vh] object-contain"
                    controls
                    autoPlay
                  />
                </div>
                
                {/* Video info */}
                <div className="p-4 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-gray-400">
                      <Heart className="w-4 h-4" /> {selectedVideo.likes || 0} likes
                    </span>
                    {user?.is_subscribed && !isOwnProfile && (
                      <Button 
                        onClick={(e) => likeVideo(selectedVideo.id, e)}
                        className="btn-outline-gold flex items-center gap-2 text-sm"
                      >
                        <Heart className="w-4 h-4" /> Like
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Album Detail Modal */}
        <Dialog open={selectedAlbum !== null} onOpenChange={(open) => !open && setSelectedAlbum(null)}>
          <DialogContent className="bg-dark-paper border-white/10 max-w-4xl max-h-[90vh] overflow-hidden">
            {selectedAlbum && (
              <div className="flex flex-col">
                <DialogHeader>
                  <DialogTitle className="font-serif text-xl text-gold flex items-center gap-2">
                    {selectedAlbum.name}
                    {selectedAlbum.is_private && <Lock className="w-4 h-4" />}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="mt-4 max-h-[60vh] overflow-y-auto">
                  {(selectedAlbum.media?.length > 0 || selectedAlbum.photos?.length > 0) ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {[...(selectedAlbum.media || []), ...(selectedAlbum.photos || [])].map((media, index) => {
                        const isVideo = media.type === 'video' || media.url?.match(/\.(mp4|webm|mov|avi)$/i);
                        return (
                          <div 
                            key={media.id || index}
                            className="aspect-square relative group cursor-pointer overflow-hidden rounded-sm bg-dark-subtle hover:ring-2 hover:ring-gold/50 transition-all"
                            onClick={() => openAlbumPhotoModal(index)}
                            data-testid={`album-photo-${index}`}
                          >
                            {isVideo ? (
                              <>
                                <video
                                  src={getImageUrl(media.url)}
                                  className="w-full h-full object-cover"
                                  muted
                                  preload="metadata"
                                />
                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                  <Play className="w-10 h-10 text-white" />
                                </div>
                                <div className="absolute top-2 left-2">
                                  <Video className="w-4 h-4 text-gold" />
                                </div>
                              </>
                            ) : (
                              <img
                                src={getImageUrl(media.url)}
                                alt=""
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.classList.add('flex', 'items-center', 'justify-center'); e.target.insertAdjacentHTML('afterend', '<span class="text-gray-500 text-xs">Image indisponible</span>'); }}
                              />
                            )}
                            <div className="absolute bottom-2 left-2">
                              <span className="flex items-center gap-1 bg-black/50 px-2 py-1 rounded text-xs text-white">
                                <Heart className="w-3 h-3" /> {media.likes || 0}
                              </span>
                            </div>
                            {/* Hover overlay */}
                            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <span className="text-white text-sm font-medium">Voir</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 py-8">
                      Aucun média dans cet album
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Album Photo Viewer Modal - Same layout as gallery photo modal */}
        <Dialog open={selectedAlbumPhotoIndex !== null} onOpenChange={(open) => !open && closeAlbumPhotoModal()}>
          <DialogContent className="bg-dark-paper border-white/10 max-w-5xl max-h-[90vh] overflow-hidden p-0">
            {selectedAlbumPhoto && (
              <div className="flex flex-col md:flex-row h-full">
                {/* Photo/Video with Navigation */}
                <div className="relative flex-1 flex items-center justify-center bg-black min-h-[300px] md:min-h-[500px]">
                  {/* Previous button */}
                  {getAlbumPhotos().length > 1 && (
                    <button
                      onClick={goToPreviousAlbumPhoto}
                      className="absolute left-2 md:left-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
                      data-testid="album-photo-prev"
                    >
                      <ChevronLeft className="w-6 h-6 md:w-8 md:h-8" />
                    </button>
                  )}
                  
                  {/* Media */}
                  {(selectedAlbumPhoto.type === 'video' || selectedAlbumPhoto.url?.match(/\.(mp4|webm|mov|avi)$/i)) ? (
                    <video
                      src={getImageUrl(selectedAlbumPhoto.url)}
                      className="max-w-full max-h-[50vh] md:max-h-[80vh] object-contain"
                      controls
                      autoPlay
                    />
                  ) : (
                    <img
                      src={getImageUrl(selectedAlbumPhoto.url)}
                      alt=""
                      className="max-w-full max-h-[50vh] md:max-h-[80vh] object-contain"
                      onError={(e) => { e.target.src = ''; e.target.alt = 'Image indisponible - veuillez re-uploader cette photo'; e.target.className = 'text-gray-400 text-sm p-4'; }}
                    />
                  )}
                  
                  {/* Next button */}
                  {getAlbumPhotos().length > 1 && (
                    <button
                      onClick={goToNextAlbumPhoto}
                      className="absolute right-2 md:right-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
                      data-testid="album-photo-next"
                    >
                      <ChevronRight className="w-6 h-6 md:w-8 md:h-8" />
                    </button>
                  )}
                  
                  {/* Photo counter */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/60 rounded-full text-white text-sm">
                    {selectedAlbumPhotoIndex + 1} / {getAlbumPhotos().length}
                  </div>
                </div>
                
                {/* Sidebar - Likes and Comments (same as gallery) */}
                <div className="w-full md:w-80 lg:w-96 flex flex-col p-4 md:p-6 border-t md:border-t-0 md:border-l border-white/10 max-h-[40vh] md:max-h-none overflow-y-auto">
                  {/* Photo info */}
                  <div className="flex items-center justify-between pb-4 border-b border-white/10">
                    <span className="flex items-center gap-2 text-gray-400">
                      <Heart className="w-4 h-4" /> {selectedAlbumPhoto.likes || 0} likes
                    </span>
                    {user?.is_subscribed && !isOwnProfile && (
                      <Button 
                        onClick={() => likePhoto(selectedAlbumPhoto.id)}
                        className="btn-outline-gold flex items-center gap-2 text-sm"
                        data-testid="album-photo-like"
                      >
                        <Heart className="w-4 h-4" /> LIKE
                      </Button>
                    )}
                  </div>
                  
                  {/* Comments */}
                  <PhotoComments 
                    photoId={selectedAlbumPhoto.id} 
                    photoOwnerId={profile?.user_id}
                  />
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Share Private Albums Modal */}
        <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
          <DialogContent className="bg-dark-paper border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl text-gold text-center">
                Partager à {profile?.pseudo}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Share All Option */}
              <div className="space-y-4">
                <div 
                  className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-colors ${
                    shareMode === 'all' ? 'border-gold bg-gold/10' : 'border-white/20 hover:border-white/40'
                  }`}
                  onClick={() => setShareMode('all')}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      shareMode === 'all' ? 'border-gold' : 'border-gray-500'
                    }`}>
                      {shareMode === 'all' && <div className="w-2.5 h-2.5 rounded-full bg-gold" />}
                    </div>
                    <span className="text-white">Tout mon contenu privé</span>
                  </div>
                  <Select 
                    value={allShareDuration} 
                    onValueChange={setAllShareDuration}
                    disabled={shareMode !== 'all'}
                  >
                    <SelectTrigger className="w-32 bg-dark-subtle border-white/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-dark-paper border-white/10">
                      {DURATION_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-white/10">
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Separator */}
              <div className="border-t border-white/10 pt-4">
                <p className="text-white text-sm text-center mb-4">
                  Choisissez seulement les albums privés que vous souhaitez partager
                </p>
              </div>

              {/* Specific Albums */}
              <div className="space-y-3 max-h-48 overflow-y-auto">
                {myPrivateAlbums.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">
                    Vous n'avez pas encore d'albums privés
                  </p>
                ) : (
                  myPrivateAlbums.map(album => (
                    <div 
                      key={album.id}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                        selectedAlbumsToShare[album.id] && shareMode === 'specific' 
                          ? 'border-gold bg-gold/10' 
                          : 'border-white/20'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id={album.id}
                          checked={selectedAlbumsToShare[album.id] || false}
                          onCheckedChange={(checked) => {
                            handleAlbumSelectionChange(album.id, checked);
                            if (checked) setShareMode('specific');
                          }}
                          className="border-white/40 data-[state=checked]:bg-gold data-[state=checked]:border-gold"
                        />
                        <label htmlFor={album.id} className="text-white text-sm cursor-pointer">
                          {album.name}
                        </label>
                      </div>
                      <Select 
                        value={shareDurations[album.id] || 'unlimited'} 
                        onValueChange={(v) => handleAlbumDurationChange(album.id, v)}
                        disabled={!selectedAlbumsToShare[album.id]}
                      >
                        <SelectTrigger className="w-32 bg-dark-subtle border-white/20 text-white text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-dark-paper border-white/10">
                          {DURATION_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-white/10 text-sm">
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))
                )}
              </div>
            </div>

            <DialogFooter className="flex-col gap-3">
              <Button 
                onClick={sharePrivateAccess}
                disabled={sharingAccess || (shareMode === 'specific' && !Object.values(selectedAlbumsToShare).some(v => v))}
                className="w-full btn-gold"
              >
                {sharingAccess ? (
                  <div className="w-5 h-5 border-2 border-dark/30 border-t-dark rounded-full animate-spin" />
                ) : (
                  'Confirmer'
                )}
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-white"
              >
                Annuler
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Subscription Popup for non-subscribers */}
      <SubscriptionPopup 
        isOpen={showSubscriptionPopup} 
        onClose={() => setShowSubscriptionPopup(false)}
        triggerSource="profile"
      />

      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        userId={userId}
        userPseudo={profile?.pseudo}
      />
    </div>
  );
}
